import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { createOrderWithPayment, verifyPaystackPayment } from "../api/api";
import {
  FaCreditCard,
  FaLock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaShieldAlt,
  FaReceipt,
  FaUser,
  FaSpinner,
  FaDownload,
  FaPrint,
  FaCalendarAlt,
  FaTag,
  FaIdCard,
  FaFileAlt,
  FaGraduationCap,
  FaFileInvoiceDollar,
  FaClock,
  FaPaperclip
} from "react-icons/fa";
import "./PaymentForm.css";

const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout, token: contextToken, login } = useContext(AuthContext);

  // Paystack configuration - Live keys
  const PAYSTACK_PUBLIC_KEY = 'pk_live_2ae636aaa999393c96ed6bee938ce3439b61a884';

  const [orderData, setOrderData] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [amount, setAmount] = useState(0);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [backendOrderId, setBackendOrderId] = useState(null);
  
  // Paystack specific state
  const [paystackLoaded, setPaystackLoaded] = useState(false);
  const [paystackEmail, setPaystackEmail] = useState("");
  const [paystackPhone, setPaystackPhone] = useState("");
  const [paystackReference, setPaystackReference] = useState(null);
  
  // Store complete order and payment data
  const [successData, setSuccessData] = useState({
    order: null,
    payment: null,
    transactionId: "",
    paymentMethod: "Credit/Debit Card",
    date: new Date().toISOString(),
    priceBreakdown: null
  });

  // Load Paystack script
  useEffect(() => {
    if (window.PaystackPop) {
      console.log("✅ Paystack already loaded");
      setPaystackLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    
    script.onload = () => {
      console.log("✅ Paystack script loaded successfully");
      setPaystackLoaded(true);
    };
    
    script.onerror = () => {
      setErrorMessage("Payment gateway failed to load. Please refresh the page.");
    };
    
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Get token from multiple sources
  const getEffectiveToken = useCallback(() => {
    const tokens = [
      contextToken,
      localStorage.getItem('authToken'),
      localStorage.getItem('token'),
      sessionStorage.getItem('authToken'),
      sessionStorage.getItem('token')
    ];
    
    return tokens.find(t => t && t.length > 30);
  }, [contextToken]);

  // Validate token with backend
  const validateToken = async (tokenToValidate) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/accounts/current-user/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${tokenToValidate}`
        },
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        return { valid: true, userData: data.user };
      } else {
        return { valid: false };
      }
    } catch (error) {
      return { valid: false, error: error.message };
    }
  };

  // Load data
  useEffect(() => {
    console.log("🔍 Payment MOUNTED - Starting data load");
    
    const orderFromState = location.state?.orderData;
    const apiDataFromState = location.state?.apiData;
    const amountFromState = location.state?.amount;
    const filesFromState = location.state?.files || [];
    
    if (orderFromState && amountFromState) {
      console.log("✅ Got data from location state");
      setOrderData(orderFromState);
      setApiData(apiDataFromState || orderFromState);
      setAmount(amountFromState);
      setFiles(filesFromState);
      
      // Set paystack email from user or order data
      if (user?.email) {
        setPaystackEmail(user.email);
      } else if (orderFromState?.email) {
        setPaystackEmail(orderFromState.email);
      }
      
      sessionStorage.setItem('orderData', JSON.stringify(orderFromState));
      if (apiDataFromState) {
        sessionStorage.setItem('orderDataAPI', JSON.stringify(apiDataFromState));
      }
    } else {
      const orderFromStorage = JSON.parse(sessionStorage.getItem('orderData'));
      const apiDataFromStorage = JSON.parse(sessionStorage.getItem('orderDataAPI'));
      
      if (orderFromStorage) {
        console.log("✅ Got data from sessionStorage");
        setOrderData(orderFromStorage);
        setApiData(apiDataFromStorage || orderFromStorage);
        setAmount(orderFromStorage.priceBreakdown?.total || 0);
        if (orderFromStorage.files) {
          setFiles(orderFromStorage.files);
        }
        
        if (user?.email) {
          setPaystackEmail(user.email);
        } else if (orderFromStorage?.email) {
          setPaystackEmail(orderFromStorage.email);
        }
      } else {
        console.log("❌ No order data found, redirecting to order page");
        navigate("/order");
      }
    }
  }, [location.state, navigate, user]);

  // Auto-refresh auth on mount
  useEffect(() => {
    const checkAndRefreshAuth = async () => {
      const effectiveToken = getEffectiveToken();
      
      if (effectiveToken && (!isAuthenticated || !contextToken)) {
        console.log("🔄 Attempting to refresh auth state from stored token...");
        const validation = await validateToken(effectiveToken);
        
        if (validation.valid && validation.userData) {
          console.log("✅ Restoring auth from valid token");
          login(validation.userData, effectiveToken);
        }
      }
    };
    
    checkAndRefreshAuth();
  }, [getEffectiveToken, isAuthenticated, contextToken, login]);

  // ========== PAYSTACK PAYMENT - CARD ONLY ==========
  const initializePaystackPayment = () => {
    try {
      console.log("💰 Initializing Paystack payment...");
      
      // Validate email
      if (!paystackEmail || !paystackEmail.includes('@')) {
        setErrorMessage("Please enter a valid email address");
        return;
      }
      
      if (!window.PaystackPop) {
        setErrorMessage("Payment gateway not loaded. Please refresh the page.");
        return;
      }
      
      setIsLoading(true);
      setErrorMessage("");
      
      // Generate a unique reference
      const reference = `PAY-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
      setPaystackReference(reference);
      
      // Convert USD amount to KES (using a fixed rate)
      const KES_RATE = 130;
      const amountInKES = Math.round(amount * KES_RATE);
      
      // Paystack expects amount in cents/kobo (multiply by 100)
      const amountInKobo = amountInKES * 100;
      
      // Prepare metadata
      const metadata = {
        order_title: orderData?.title || "Research Paper",
        user_id: user?.id || "guest",
        user_name: user?.username || "Guest User",
        files_count: files.length,
        original_amount_usd: amount,
        converted_amount_kes: amountInKES,
        conversion_rate: KES_RATE,
        payment_type: "card_only",
        custom_fields: [
          {
            display_name: "Order Title",
            variable_name: "order_title",
            value: orderData?.title || "Research Paper"
          },
          {
            display_name: "Customer Name",
            variable_name: "customer_name",
            value: user?.username || "Guest User"
          },
          {
            display_name: "Amount in USD",
            variable_name: "amount_usd",
            value: amount.toString()
          }
        ]
      };
      
      // Initialize Paystack with CARD ONLY
      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: paystackEmail,
        amount: amountInKobo,
        currency: "KES",
        ref: reference,
        metadata: metadata,
        phone: paystackPhone,
        channels: ['card'], // CARD ONLY - removes all other payment options
        callback: function(response) {
          console.log("✅ Paystack callback received:", response);
          verifyAndCompletePayment(response.reference);
        },
        onClose: function() {
          console.log("❌ Paystack modal closed by user");
          setIsLoading(false);
          setErrorMessage("Payment cancelled. Please try again.");
        }
      });
      
      // Open the payment modal
      handler.openIframe();
      
    } catch (error) {
      console.error("❌ Paystack initialization error:", error);
      setErrorMessage("Failed to initialize payment. Please try again.");
      setIsLoading(false);
    }
  };

  // Verify payment with backend
  const verifyAndCompletePayment = async (reference) => {
    try {
      console.log("🔍 Verifying payment with backend:", reference);
      
      const verificationResult = await verifyPaystackPayment(reference);
      console.log("Verification result:", verificationResult);
      
      if (!verificationResult.success) {
        setErrorMessage("Payment verification failed. Please contact support.");
        setIsLoading(false);
        return;
      }
      
      await createOrderAfterPaystack(verificationResult.data);
      
    } catch (error) {
      console.error("❌ Payment verification error:", error);
      
      setPaymentSuccess(true);
      setSuccessData({
        order: null,
        payment: null,
        transactionId: reference,
        paymentMethod: "Credit/Debit Card",
        date: new Date().toISOString(),
        priceBreakdown: orderData?.priceBreakdown
      });
      setWarningMessage("Payment was successful but verification failed. Please save your transaction ID and contact support.");
      setIsLoading(false);
    }
  };

  // Create order after successful Paystack payment
  const createOrderAfterPaystack = async (paystackData) => {
    try {
      console.log("📦 Creating order after Paystack payment...");
      
      const KES_RATE = 130;
      const amountInKES = Math.round(amount * KES_RATE);
      
      const effectiveToken = getEffectiveToken();
      
      if (!effectiveToken) {
        // Handle guest checkout
        console.log("Guest checkout - saving payment data for registration");
        const redirectData = {
          orderData,
          apiData,
          amount,
          files,
          paystackData,
          transactionId: paystackData.reference || paystackReference,
          paymentMethod: "Credit/Debit Card"
        };
        
        sessionStorage.setItem('guestPaymentData', JSON.stringify(redirectData));
        
        setPaymentSuccess(true);
        setSuccessData({
          order: null,
          payment: null,
          transactionId: paystackData.reference || paystackReference,
          paymentMethod: "Credit/Debit Card",
          date: new Date().toISOString(),
          priceBreakdown: orderData?.priceBreakdown
        });
        
        setIsLoading(false);
        return;
      }
      
      // Create FormData to send files
      const formData = new FormData();
      
      // Prepare order data
      const orderPayload = apiData || {
        title: orderData?.title || "Untitled Paper",
        subject: orderData?.subject || "General",
        academic_level: String(orderData?.academic_level || "High School").trim(),
        pages: parseInt(orderData?.pages || 1),
        deadline: orderData?.deadline || new Date().toISOString(),
        instructions: orderData?.instructions || "No instructions provided",
        budget: parseFloat(amount) || 0,
        language: orderData?.language || "English",
        format: orderData?.format || "APA",
        spacing: orderData?.spacing || "Double",
        slides: parseInt(orderData?.slides || 0),
        sources: parseInt(orderData?.sources || 0),
        plagiarism_report: Boolean(orderData?.plagiarismReport || orderData?.plagiarism_report),
        executive_summary: Boolean(orderData?.executiveSummary || orderData?.executive_summary),
        expert_writer: Boolean(orderData?.expertWriter || orderData?.expert_writer),
        price_breakdown: orderData?.priceBreakdown || {
          basePrice: amount * 0.8,
          rushFee: amount * 0.1,
          serviceFee: amount * 0.1,
          total: amount
        },
      };
      
      // Prepare payment data with Paystack details
      const transactionId = paystackData.reference || paystackReference;
      const paymentPayload = {
        amount: parseFloat(amount),
        payment_method: "CREDIT_CARD",
        transaction_id: transactionId,
        notes: `Payment via Credit Card. Reference: ${transactionId}`,
        metadata: JSON.stringify({
          payment_method_display: "Credit/Debit Card",
          user_email: paystackEmail,
          order_title: orderPayload.title,
          paystack_reference: transactionId,
          paystack_transaction_id: paystackData.transaction_id,
          paystack_customer_email: paystackData.customer_email,
          paid_at: paystackData.paid_at,
          currency: "KES",
          amount_kes: amountInKES
        })
      };
      
      // Add order and payment to FormData
      formData.append('order', JSON.stringify(orderPayload));
      formData.append('payment', JSON.stringify(paymentPayload));
      
      // Add files
      if (files && files.length > 0) {
        console.log(`📁 Adding ${files.length} files to FormData...`);
        files.forEach((file, index) => {
          let fileToUpload = file;
          if (file instanceof File) {
            fileToUpload = file;
          } else if (file.file) {
            fileToUpload = file.file;
          }
          
          if (fileToUpload && fileToUpload instanceof File) {
            formData.append(`file_${index}`, fileToUpload);
          }
        });
      }
      
      const result = await createOrderWithPayment(formData);
      console.log("✅ Combined result:", result);
      
      if (result.success) {
        setPaymentSuccess(true);
        setSuccessMessage(result.message || "Order and payment created successfully!");
        
        setSuccessData({
          order: result.order,
          payment: result.payment,
          transactionId: result.payment?.transaction_id || transactionId,
          paymentMethod: "Credit/Debit Card",
          date: new Date().toISOString(),
          priceBreakdown: result.order?.price_breakdown || orderData?.priceBreakdown
        });
        
        if (result.order?.id) {
          setBackendOrderId(result.order.id);
        }
        
        // Clear session storage
        sessionStorage.removeItem('orderData');
        sessionStorage.removeItem('orderDataAPI');
        sessionStorage.removeItem('paymentRedirectData');
        sessionStorage.removeItem('guestPaymentData');
        
      } else {
        setPaymentSuccess(true);
        setSuccessData({
          order: null,
          payment: null,
          transactionId: transactionId,
          paymentMethod: "Credit/Debit Card",
          date: new Date().toISOString(),
          priceBreakdown: orderData?.priceBreakdown
        });
        setWarningMessage("Payment successful but order creation failed. Please contact support with your transaction ID: " + transactionId);
      }
      
    } catch (error) {
      console.error("❌ Order creation after Paystack error:", error);
      
      setPaymentSuccess(true);
      setSuccessData({
        order: null,
        payment: null,
        transactionId: paystackReference || "Unknown",
        paymentMethod: "Credit/Debit Card",
        date: new Date().toISOString(),
        priceBreakdown: orderData?.priceBreakdown
      });
      setWarningMessage("Payment was successful but there was an issue creating your order. Please save your transaction ID and contact support.");
    } finally {
      setIsLoading(false);
    }
  };

  // ========== RECEIPT FUNCTIONS ==========
  const downloadReceipt = () => {
    const receiptContent = generateReceiptHTML();
    
    const blob = new Blob([receiptContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${successData.transactionId}_ResearchWriting.html`;
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateReceiptHTML = () => {
    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const formattedTime = now.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    const KES_RATE = 130;
    const amountInKES = Math.round(amount * KES_RATE);

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Payment Receipt - Research Writing</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 40px; color: #333; background-color: #f8f9fa; }
        .receipt-container { max-width: 800px; margin: 0 auto; background: white; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); overflow: hidden; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px; text-align: center; }
        .header h1 { margin: 0; font-size: 32px; }
        .content { padding: 40px; }
        .section { margin-bottom: 30px; }
        .section-title { color: #667eea; border-bottom: 2px solid #667eea; padding-bottom: 10px; margin-bottom: 20px; }
        .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
        .info-item { margin-bottom: 15px; }
        .info-label { font-weight: 600; color: #666; font-size: 14px; margin-bottom: 5px; }
        .info-value { font-size: 16px; color: #333; }
        .payment-details { background: #f8f9fa; border-radius: 8px; padding: 25px; margin-top: 30px; }
        .amount-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #dee2e6; }
        .amount-total { border-top: 2px solid #667eea; border-bottom: none; font-weight: 700; font-size: 18px; margin-top: 10px; padding-top: 15px; }
        .footer { text-align: center; padding: 30px; background: #f8f9fa; color: #666; font-size: 14px; border-top: 1px solid #dee2e6; }
        .status-badge { display: inline-block; background: #28a745; color: white; padding: 8px 16px; border-radius: 20px; font-weight: 600; }
        .timestamp { text-align: center; color: #666; margin-bottom: 30px; font-size: 14px; }
        .warning-note { background: #fff3cd; border: 1px solid #ffeeba; color: #856404; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
    </style>
</head>
<body>
    <div class="receipt-container">
        <div class="header">
            <h1>Payment Successful!</h1>
            <p>Thank you for your payment. Your order has been confirmed.</p>
            <div style="margin-top: 20px;"><span class="status-badge">✓ PAID</span></div>
        </div>
        
        <div class="timestamp">${formattedDate}, ${formattedTime}</div>
        
        <div class="content">
            ${warningMessage ? `<div class="warning-note"><strong>Note:</strong> ${warningMessage}</div>` : ''}
            
            <div class="section">
                <h2 class="section-title">Order Confirmation</h2>
                <div class="info-grid">
                    <div class="info-item">
                        <div class="info-label">Order ID</div>
                        <div class="info-value">ORD-${successData.order?.id || backendOrderId || "N/A"}</div>
                    </div>
                    <div class="info-item">
                        <div class="info-label">Transaction ID</div>
                        <div class="info-value">${successData.transactionId || "N/A"}</div>
                    </div>
                    <div class="info-item">
                        <div class="info-label">Payment Method</div>
                        <div class="info-value">${successData.paymentMethod}</div>
                    </div>
                    <div class="info-item">
                        <div class="info-label">Date</div>
                        <div class="info-value">${formattedDate}</div>
                    </div>
                </div>
            </div>
            
            <div class="section">
                <h2 class="section-title">Paper Details</h2>
                <div class="info-grid">
                    <div class="info-item">
                        <div class="info-label">Title</div>
                        <div class="info-value">${orderData?.title || "N/A"}</div>
                    </div>
                    <div class="info-item">
                        <div class="info-label">Academic Level</div>
                        <div class="info-value">${orderData?.academic_level || "N/A"}</div>
                    </div>
                    <div class="info-item">
                        <div class="info-label">Pages</div>
                        <div class="info-value">${orderData?.pages || 0} pages</div>
                    </div>
                </div>
            </div>
            
            <div class="payment-details">
                <h2 class="section-title">Payment Summary</h2>
                <div class="amount-row">
                    <span>Base Price (USD)</span>
                    <span>$${(successData.priceBreakdown?.basePrice || (amount * 0.8)).toFixed(2)}</span>
                </div>
                ${successData.priceBreakdown?.rushFee > 0 ? `<div class="amount-row"><span>Rush Fee (USD)</span><span>+$${successData.priceBreakdown.rushFee.toFixed(2)}</span></div>` : ''}
                ${successData.priceBreakdown?.serviceFee > 0 ? `<div class="amount-row"><span>Additional Services (USD)</span><span>+$${successData.priceBreakdown.serviceFee.toFixed(2)}</span></div>` : ''}
                <div class="amount-row amount-total">
                    <strong>Total Paid (USD)</strong>
                    <strong>$${amount}</strong>
                </div>
                <div class="amount-row">
                    <strong>Amount in KES:</strong>
                    <strong>KES ${amountInKES.toLocaleString()}</strong>
                </div>
            </div>
        </div>
        
        <div class="footer">
            <p>Research Writing Service<br>If you have any questions, contact our support team at support@researchwriting.com</p>
            <p style="margin-top: 10px; font-size: 12px;">Receipt generated on ${formattedDate} at ${formattedTime}</p>
        </div>
    </div>
</body>
</html>`;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "Not set";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return '📎';
    const ext = fileName.split('.').pop().toLowerCase();
    if (ext === 'pdf') return '📕';
    if (['doc', 'docx'].includes(ext)) return '📄';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return '🖼️';
    return '📎';
  };

  const handlePayNow = () => {
    initializePaystackPayment();
  };

  if (!orderData) {
    return (
      <div className="payment-loading">
        <div className="payment-spinner"></div>
        <p>Loading payment details...</p>
      </div>
    );
  }

  const KES_RATE = 130;
  const amountInKES = Math.round(amount * KES_RATE);

  return (
    <div className="payment-container">
      <div className="payment-background-overlay"></div>
      
      <div className="payment-form-container">
        {paymentSuccess ? (
          // SUCCESS SCREEN
          <div className="payment-success-screen">
            <div className="success-header">
              <FaCheckCircle className="success-main-icon" />
              <h1 className="success-title">Payment Successful!</h1>
              <p className="success-subtitle">
                Thank you for your payment. Your order has been confirmed.
              </p>
              <div className="status-badge">
                <FaCheckCircle className="status-icon" />
                <span>Paid</span>
              </div>
            </div>

            {warningMessage && (
              <div className="warning-alert">
                <FaExclamationTriangle className="warning-icon" />
                <span>{warningMessage}</span>
              </div>
            )}

            <div className="success-timestamp">
              <FaCalendarAlt className="timestamp-icon" />
              <span>{new Date().toLocaleDateString('en-GB')}, {new Date().toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'})}</span>
            </div>

            <div className="success-content">
              <div className="success-section">
                <h2 className="section-title">
                  <FaIdCard className="section-icon" />
                  Order Confirmation
                </h2>
                <div className="confirmation-grid">
                  <div className="confirmation-item">
                    <div className="confirmation-label">
                      <FaTag className="label-icon" />
                      Order ID
                    </div>
                    <div className="confirmation-value">
                      ORD-{successData.order?.id || backendOrderId || "N/A"}
                    </div>
                  </div>
                  <div className="confirmation-item">
                    <div className="confirmation-label">
                      <FaReceipt className="label-icon" />
                      Transaction ID
                    </div>
                    <div className="confirmation-value">
                      {successData.transactionId}
                    </div>
                  </div>
                  <div className="confirmation-item">
                    <div className="confirmation-label">
                      <FaCreditCard className="label-icon" />
                      Payment Method
                    </div>
                    <div className="confirmation-value">
                      {successData.paymentMethod}
                    </div>
                  </div>
                  <div className="confirmation-item">
                    <div className="confirmation-label">
                      <FaCalendarAlt className="label-icon" />
                      Date
                    </div>
                    <div className="confirmation-value">
                      {new Date(successData.date).toLocaleDateString('en-GB')}
                    </div>
                  </div>
                </div>
              </div>

              <div className="success-section">
                <h2 className="section-title">
                  <FaFileAlt className="section-icon" />
                  Paper Details
                </h2>
                <div className="paper-grid">
                  <div className="paper-item">
                    <div className="paper-label">Title</div>
                    <div className="paper-value">{orderData?.title}</div>
                  </div>
                  <div className="paper-item">
                    <div className="paper-label">
                      <FaGraduationCap className="paper-icon" />
                      Academic Level
                    </div>
                    <div className="paper-value">{orderData?.academic_level}</div>
                  </div>
                  <div className="paper-item">
                    <div className="paper-label">
                      <FaFileAlt className="paper-icon" />
                      Pages
                    </div>
                    <div className="paper-value">{orderData?.pages} pages</div>
                  </div>
                  <div className="paper-item">
                    <div className="paper-label">
                      <FaClock className="paper-icon" />
                      Deadline
                    </div>
                    <div className="paper-value">
                      {formatDateForDisplay(orderData?.deadline) || "Not set"}
                    </div>
                  </div>
                </div>
              </div>

              {files.length > 0 && (
                <div className="success-section">
                  <h2 className="section-title">
                    <FaPaperclip className="section-icon" />
                    Uploaded Files
                  </h2>
                  <div className="files-grid">
                    {files.map((file, index) => (
                      <div key={index} className="file-item">
                        <div className="file-icon">{getFileIcon(file.name)}</div>
                        <div className="file-info">
                          <div className="file-name">{file.name}</div>
                          <div className="file-size">{formatFileSize(file.size)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!isAuthenticated && (
                <div className="success-section">
                  <div className="guest-checkout-note">
                    <FaInfoCircle className="note-icon" />
                    <p>
                      Your payment was successful! Please create an account to complete your order and track its progress.
                    </p>
                    <button
                      onClick={() => navigate("/register", { 
                        state: { 
                          paymentCompleted: true,
                          transactionId: successData.transactionId,
                          orderData,
                          files
                        } 
                      })}
                      className="success-action-btn primary"
                    >
                      Create Account
                    </button>
                  </div>
                </div>
              )}

              <div className="success-section payment-summary-section">
                <h2 className="section-title">
                  <FaFileInvoiceDollar className="section-icon" />
                  Payment Summary
                </h2>
                <div className="payment-breakdown">
                  <div className="breakdown-row">
                    <span>Base Price (USD)</span>
                    <span>${(successData.priceBreakdown?.basePrice || (amount * 0.8)).toFixed(2)}</span>
                  </div>
                  {successData.priceBreakdown?.rushFee > 0 && (
                    <div className="breakdown-row">
                      <span>Rush Fee (USD)</span>
                      <span>+$${successData.priceBreakdown.rushFee.toFixed(2)}</span>
                    </div>
                  )}
                  {successData.priceBreakdown?.serviceFee > 0 && (
                    <div className="breakdown-row">
                      <span>Additional Services (USD)</span>
                      <span>+$${successData.priceBreakdown.serviceFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="breakdown-row total-row">
                    <strong>Total Paid (USD)</strong>
                    <strong>${amount}</strong>
                  </div>
                  <div className="breakdown-row">
                    <strong>Amount in KES:</strong>
                    <strong>KES {amountInKES.toLocaleString()}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="success-actions">
              <button onClick={downloadReceipt} className="success-action-btn primary">
                <FaDownload className="action-icon" />
                Download Receipt
              </button>
              <button onClick={() => window.print()} className="success-action-btn">
                <FaPrint className="action-icon" />
                Print Receipt
              </button>
              <button onClick={() => navigate("/dashboard")} className="success-action-btn">
                <FaUser className="action-icon" />
                Go to Dashboard
              </button>
              <button onClick={() => navigate("/order")} className="success-action-btn">
                <FaFileAlt className="action-icon" />
                Create New Order
              </button>
            </div>

            <div className="success-footer">
              <p className="footer-brand">Research Writing Service</p>
              <p className="footer-note">
                If you have any questions, contact our support team at support@researchwriting.com
              </p>
            </div>
          </div>
        ) : (
          // SIMPLIFIED PAYMENT SCREEN - DIRECT TO PAYSTACK
          <>
            <div className="payment-header">
              <div className="payment-icon-container">
                <FaCreditCard className="payment-main-icon" />
              </div>
              <div className="payment-header-content">
                <h1 className="payment-title">Secure Payment</h1>
                <p className="payment-subtitle">
                  Complete your payment to finalize your order
                </p>
              </div>
              
              {isAuthenticated && user ? (
                <div className="payment-user-badge">
                  <FaUser className="payment-user-icon" />
                  <div className="payment-user-info">
                    <span className="payment-username">{user?.username}</span>
                    <button onClick={logout} className="payment-logout-btn">Logout</button>
                  </div>
                </div>
              ) : (
                <div className="payment-login-reminder-badge">
                  <FaInfoCircle className="payment-reminder-icon" />
                  <div className="payment-reminder-info">
                    <span className="payment-reminder-text">Guest Checkout</span>
                    <button 
                      onClick={() => navigate("/login", { 
                        state: { 
                          message: "Login to track your order",
                          redirectTo: "/payment",
                          redirectData: { orderData, apiData, amount, files }
                        } 
                      })}
                      className="payment-login-btn"
                    >
                      Login
                    </button>
                  </div>
                </div>
              )}
              
              <div className="payment-security-badge">
                <FaLock className="payment-security-icon" />
                <span>256-bit SSL Secured</span>
              </div>
            </div>

            {!isAuthenticated && (
              <div className="payment-login-reminder">
                <FaInfoCircle className="payment-reminder-icon" />
                <div className="payment-reminder-content">
                  <p>
                    <strong>Guest checkout:</strong> You can pay now and create an account after payment.
                  </p>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="payment-success-alert">
                <FaCheckCircle className="payment-success-icon" />
                <span>{successMessage}</span>
              </div>
            )}

            {errorMessage && (
              <div className="payment-error-alert">
                <FaExclamationTriangle className="payment-error-icon" />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Email input for Paystack */}
            <div className="payment-email-section">
              <label className="payment-email-label">
                <FaUser className="email-icon" />
                Email for Receipt
              </label>
              <input
                type="email"
                value={paystackEmail}
                onChange={(e) => setPaystackEmail(e.target.value)}
                placeholder="your@email.com"
                className="payment-email-input"
                disabled={!!user?.email}
              />
              <p className="payment-email-hint">We'll send your receipt to this email</p>
            </div>

            <div className="payment-order-summary">
              <h3 className="payment-order-title">
                <FaReceipt className="payment-order-icon" />
                Order Summary
              </h3>
              
              <div className="payment-order-details">
                <div className="payment-order-row">
                  <span>Paper Title</span>
                  <span className="payment-order-value">{orderData.title}</span>
                </div>
                <div className="payment-order-row">
                  <span>Academic Level</span>
                  <span className="payment-order-value">{orderData.academic_level}</span>
                </div>
                <div className="payment-order-row">
                  <span>Pages</span>
                  <span className="payment-order-value">{orderData.pages} pages</span>
                </div>
                <div className="payment-order-row">
                  <span>Deadline</span>
                  <span className="payment-order-value">
                    {formatDateForDisplay(orderData.deadline)}
                  </span>
                </div>
                <div className="payment-order-row">
                  <span>Attached Files</span>
                  <span className="payment-order-value">{files.length} file(s)</span>
                </div>
                <div className="payment-order-row payment-order-total">
                  <span>Amount (USD)</span>
                  <strong>${amount}</strong>
                </div>
                <div className="payment-order-row payment-order-total">
                  <span>Total to Pay (KES)</span>
                  <strong className="payment-order-amount">KES {amountInKES.toLocaleString()}</strong>
                </div>
              </div>
            </div>

            {files.length > 0 && (
              <div className="payment-files-section">
                <h3 className="payment-section-title">
                  <FaPaperclip className="payment-section-icon" />
                  Files to be Uploaded
                </h3>
                <div className="payment-files-grid">
                  {files.map((file, index) => (
                    <div key={index} className="payment-file-item">
                      <div className="payment-file-icon">{getFileIcon(file.name)}</div>
                      <div className="payment-file-info">
                        <div className="payment-file-name">{file.name}</div>
                        <div className="payment-file-size">{formatFileSize(file.size)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="payment-security-features">
              <div className="security-feature">
                <FaLock className="security-feature-icon" />
                <div className="security-feature-content">
                  <h4>256-bit SSL Encryption</h4>
                  <p>Your data is protected</p>
                </div>
              </div>
              
              <div className="security-feature">
                <FaCheckCircle className="security-feature-icon" />
                <div className="security-feature-content">
                  <h4>Money-Back Guarantee</h4>
                  <p>Full refund if not delivered</p>
                </div>
              </div>
              
              <div className="security-feature">
                <FaShieldAlt className="security-feature-icon" />
                <div className="security-feature-content">
                  <h4>PCI Compliant</h4>
                  <p>Industry standard security</p>
                </div>
              </div>
            </div>

            <div className="payment-action-section">
              <button
                onClick={handlePayNow}
                className="payment-pay-button"
                disabled={isLoading || !paystackEmail || !paystackLoaded}
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="payment-spinner-icon" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FaCreditCard className="payment-button-icon" />
                    Pay KES {amountInKES.toLocaleString()}
                  </>
                )}
              </button>
              
              <p className="payment-guarantee">
                <FaCheckCircle className="guarantee-icon" />
                <span>100% Satisfaction Guarantee</span>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Payment;